import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../app/store';
import { ErrorPlaceholder } from '../../../components/common/ErrorPlaceholder';
import { LoadingPlaceholder } from '../../../components/common/LoadingPlaceholder';
import { useToast } from '../../../components/toast-container';
import { CouponPerformanceTable } from './components/CouponPerformanceTable';
import { CouponLiveEventTabSwitcher, EventAnalyticsDashboardHeader, EventAnalyticsTab } from './components/EventAnalyticsDashboardHeader';
import { FiltersSection } from './components/FiltersSection';
import { AttendeeGeographicDistributionHeader } from './components/AttendeeGeographicDistributionHeader';
import { AttendeeGeographicDistributionMap } from './components/AttendeeGeographicDistributionMap';
import { EventPerformanceTable, EventPerformanceRow } from './components/EventPerformanceTable';
import { LiveEventAreaItem } from './components/LiveEventActivityLevelsPanel';
import { LiveEventFiltersSection, LiveEventFilters } from './components/LiveEventFiltersSection';
import { LiveEventMetricCards } from './components/LiveEventMetricCards';
import { HeatmapPagination } from './components/HeatmapPagination';
import { HeatMapSection } from './components/HeatMapSection';
import { MetricCards } from './components/MetricCards';
import { PlanGateOverlay } from './components/PlanGateOverlay';
import { PremiumGeographicDistribution } from './components/PremiumGeographicDistribution';
import { PremiumInsightsBar } from './components/PremiumInsightsBar';
import { MerchantHeatmapFilters } from './MerchantHeatmap.data';
import { LiveEventsMapFilter } from './MerchantLiveEventsMap.data';
import { exportMerchantHeatmapCSV, exportMerchantHeatmapXLSX, fetchMerchantHeatmapData, fetchLocationAnalytics, setSelectedLocation, updateFilters } from './MerchantHeatmap.slice';
import {
    fetchLiveEventOptions,
    fetchLiveEventLocationOptions,
    fetchLiveEventsMapAll,
    setLiveEventsMapFilter,
} from './MerchantLiveEventsMap.slice';
import { exportLiveEventsMapCSV, exportLiveEventsMapXLSX } from './MerchantLiveEventsMap.api';

type PlanType = 'free' | 'basic' | 'premium';
const PLAN_OVERRIDE_KEY = 'merchant_heatmap_plan_override';
/** Mock: show Event Analytics header + Coupon/Live Event tabs (Basic/Premium with add-on). */
const EVENT_ADDON_OVERRIDE_KEY = 'merchant_heatmap_event_addon_override';

/** Convert UI filters to API filter for live-events-map endpoints */
function liveEventFiltersToApiFilter(f: LiveEventFilters): LiveEventsMapFilter {
    const quickFilterDays = f.quickFilter === 'last_7_days' ? 7 : f.quickFilter === 'last_90_days' ? 90 : 30;
    const demographicAge = f.demographics === 'ages_18_34' ? '18-34' : f.demographics === 'ages_35_54' ? '35-54' : f.demographics === 'ages_55_plus' ? '55+' : '';
    return {
        event_ids: f.eventName && f.eventName !== 'all' ? [f.eventName] : [],
        location_ids: f.location && f.location !== 'all' ? [f.location] : [],
        demographic_age: demographicAge,
        quick_filter_days: quickFilterDays,
        start_date: f.startDate ? new Date(f.startDate).toISOString() : undefined,
        end_date: f.endDate ? new Date(f.endDate + 'T23:59:59.999Z').toISOString() : undefined,
    };
}

const MerchantHeatmapPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { data, isLoading, error } = useSelector((state: RootState) => state.merchantHeatmap);
    const liveEventsMap = useSelector((state: RootState) => state.merchantLiveEventsMap);
    const navigate = useNavigate();
    const { addToast } = useToast();
    // Default to premium with event add-on enabled
    const currentPlan: PlanType = 'premium';
    const isFree = false;
    const isBasic = false;
    const isPremium = true;
    const hasEventAddOn = true; // Event add-on enabled by default
    const [analyticsTab, setAnalyticsTab] = React.useState<EventAnalyticsTab>('coupons');
    const [liveEventFilters, setLiveEventFilters] = React.useState<LiveEventFilters>({ quickFilter: 'last_7_days' });
    const [page, setPage] = React.useState(1);
    const pageSize = 5;

    // Free: lock vertical scrolling while the page is gated
    React.useEffect(() => {
        if (typeof document === 'undefined') return;

        const prevBodyOverflow = document.body.style.overflow;
        const prevHtmlOverflow = document.documentElement.style.overflow;

        if (isFree) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = prevBodyOverflow;
            document.documentElement.style.overflow = prevHtmlOverflow;
        };
    }, [isFree]);

    useEffect(() => {
        dispatch(fetchMerchantHeatmapData(undefined));
        dispatch(fetchLocationAnalytics());
    }, [dispatch]);

    // Gọi API Live Events Map ngay khi vào trang Heatmap (có add-on): options + stats/map/activity/table
    useEffect(() => {
        if (!hasEventAddOn) return;
        const defaultFilter = liveEventFiltersToApiFilter({ quickFilter: 'last_7_days' });
        dispatch(fetchLiveEventOptions());
        dispatch(fetchLiveEventLocationOptions());
        dispatch(setLiveEventsMapFilter(defaultFilter));
        dispatch(fetchLiveEventsMapAll(defaultFilter));
    }, [dispatch, hasEventAddOn]);

    // Khi chuyển sang tab Live Events: refetch một lần với filter hiện tại
    useEffect(() => {
        if (!hasEventAddOn || analyticsTab !== 'live_events') return;
        const apiFilter = liveEventFiltersToApiFilter(liveEventFilters);
        dispatch(setLiveEventsMapFilter(apiFilter));
        dispatch(fetchLiveEventsMapAll(apiFilter));
    }, [dispatch, hasEventAddOn, analyticsTab]);


    const handleFiltersChange = (filters: MerchantHeatmapFilters) => {
        dispatch(updateFilters(filters));
    };

    const handleApplyFilters = () => {
        if (data?.filters) {
            dispatch(fetchMerchantHeatmapData(data.filters));
        }
    };

    const handleExportCSV = async () => {
        try {
            const result = await dispatch(exportMerchantHeatmapCSV(data?.filters || undefined));
            if (exportMerchantHeatmapCSV.fulfilled.match(result)) {
                const blob = result.payload;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `merchant-heatmap-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                addToast({
                    type: 'success',
                    title: 'Success',
                    message: 'Data exported successfully',
                });
            } else {
                throw new Error('Export failed');
            }
        } catch (err: any) {
            addToast({
                type: 'error',
                title: 'Error',
                message: err?.message || 'Failed to export data',
            });
        }
    };

    const handleExportXLSX = async () => {
        try {
            const result = await dispatch(exportMerchantHeatmapXLSX(data?.filters || undefined));
            if (exportMerchantHeatmapXLSX.fulfilled.match(result)) {
                const blob = result.payload;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `merchant-heatmap-${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                addToast({
                    type: 'success',
                    title: 'Success',
                    message: 'Data exported successfully',
                });
            } else {
                throw new Error('Export failed');
            }
        } catch (err: any) {
            addToast({
                type: 'error',
                title: 'Error',
                message: err?.message || 'Failed to export data',
            });
        }
    };

    const handleExportPDF = () => {
        const url = `/merchant-heatmap-pdf`;
        window.open(url, "_blank");
    };

    const handleLocationSelect = (location: any) => {
        dispatch(setSelectedLocation(location));
    };

    // Live Event export (client-side từ data hiện có, giống admin heatmap pattern)
    const handleLiveEventExportCSV = () => {
        const blob = exportLiveEventsMapCSV({
            stats: liveEventsMap.stats,
            mapLocations: liveEventsMap.mapLocations,
            activityLevels: liveEventsMap.activityLevels,
            eventPerformanceRows: liveEventsMap.eventPerformanceRows,
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `live-events-map-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        addToast({ type: 'success', title: 'Success', message: 'Export CSV completed' });
    };
    const handleLiveEventExportXLSX = () => {
        const blob = exportLiveEventsMapXLSX({
            stats: liveEventsMap.stats,
            mapLocations: liveEventsMap.mapLocations,
            activityLevels: liveEventsMap.activityLevels,
            eventPerformanceRows: liveEventsMap.eventPerformanceRows,
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `live-events-map-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        addToast({ type: 'success', title: 'Success', message: 'Export completed' });
    };
    const handleLiveEventExportPDF = () => {
        // Admin heatmap: mở /heatmap-pdf. Merchant coupon: /merchant-heatmap-pdf. Live event chưa có trang PDF riêng → in trang hiện tại.
        window.print();
    };

    if (isLoading) {
        return <LoadingPlaceholder />;
    }

    if (error) {
        return <ErrorPlaceholder error={error} />;
    }

    if (!data) {
        return null;
    }

    const couponRows = data.coupon_performance ?? [];
    const totalPages = Math.max(1, Math.ceil(couponRows.length / pageSize));
    const pagedRows = couponRows.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="relative bg-white dark:bg-gray-900 min-h-screen">
            {/* Page body */}
            <div className="p-[32.8px]">
                {/* Content (blocked by Free overlay when plan is Free) */}
                <div className="relative">
                    {/* Header: Event Analytics (title + tabs) only when add-on AND Live Events tab; else Coupon header (with tab pill on right when add-on) */}
                    {hasEventAddOn && analyticsTab === 'live_events' ? (
                        <EventAnalyticsDashboardHeader activeTab={analyticsTab} onTabChange={setAnalyticsTab} />
                    ) : (
                        <div className="w-full flex flex-col sm:flex-row sm:items-start sm:justify-between gap-[6.5px]">
                            <div className="flex flex-col gap-[6.5px]">
                                <h1 className="text-[29.3px] font-bold text-[#364153] dark:text-white leading-[32.5px]" style={{ fontFamily: 'Nunito' }}>
                                    Coupon Redemption Analytics
                                </h1>
                                <p className="text-[14.6px] font-normal text-[#4a5565] dark:text-gray-400 leading-[22.75px]" style={{ fontFamily: 'Nunito' }}>
                                    Track where customers are redeeming your coupons across neighborhoods
                                </p>
                            </div>
                            {hasEventAddOn && (
                                <CouponLiveEventTabSwitcher activeTab={analyticsTab} onTabChange={setAnalyticsTab} />
                            )}
                        </div>
                    )}

                    <div className="flex flex-col gap-[32px] mt-[32px]">
                        {/* Live Events tab: header detail (KPI cards) + rest of content TBD */}
                        {hasEventAddOn && analyticsTab === 'live_events' && (
                            <div className="flex flex-col gap-8">
                                {(liveEventsMap.loading.stats || liveEventsMap.loading.map) && !liveEventsMap.stats && (
                                    <LoadingPlaceholder />
                                )}
                                {liveEventsMap.error && (
                                    <div className="text-sm text-red-600 dark:text-red-400">{liveEventsMap.error}</div>
                                )}
                                {liveEventsMap.stats && (
                                    <LiveEventMetricCards
                                        totalEvents={liveEventsMap.stats.totalEvents}
                                        totalEventsSubtext={liveEventsMap.stats.totalEventsSubtext}
                                        totalAttendees={liveEventsMap.stats.totalAttendees}
                                        totalAttendeesSubtext={liveEventsMap.stats.totalAttendeesSubtext}
                                        avgShowRatePercent={liveEventsMap.stats.avgShowRatePercent}
                                        avgShowRateSubtext={liveEventsMap.stats.avgShowRateSubtext}
                                        eventToRedemptionPercent={liveEventsMap.stats.eventToRedemptionPercent}
                                        eventToRedemptionSubtext={liveEventsMap.stats.eventToRedemptionSubtext}
                                    />
                                )}
                                <LiveEventFiltersSection
                                    filters={liveEventFilters}
                                    onFiltersChange={setLiveEventFilters}
                                    onApplyFilters={(filters) => {
                                        setLiveEventFilters(filters);
                                        const apiFilter = liveEventFiltersToApiFilter(filters);
                                        dispatch(setLiveEventsMapFilter(apiFilter));
                                        dispatch(fetchLiveEventsMapAll(apiFilter));
                                    }}
                                    onExportCSV={handleLiveEventExportCSV}
                                    onExportXLSX={handleLiveEventExportXLSX}
                                    onExportPDF={handleLiveEventExportPDF}
                                    eventOptions={liveEventsMap.eventOptions}
                                    locationOptions={liveEventsMap.locationOptions}
                                />
                                <AttendeeGeographicDistributionHeader>
                                    <AttendeeGeographicDistributionMap
                                        locations={liveEventsMap.mapLocations.length > 0 ? liveEventsMap.mapLocations : data.locations}
                                        totalAttendees={liveEventsMap.stats?.totalAttendees ?? 0}
                                        panelItems={liveEventsMap.activityLevels.length > 0 ? liveEventsMap.activityLevels : []}
                                        selectedLocation={data.selectedLocation}
                                        onLocationSelect={(loc) => dispatch(setSelectedLocation(loc))}
                                    />
                                </AttendeeGeographicDistributionHeader>
                                <EventPerformanceTable rows={liveEventsMap.eventPerformanceRows} />
                            </div>
                        )}
                        {/* Coupon content: when no add-on or when Coupons tab is selected */}
                        {(!hasEventAddOn || analyticsTab === 'coupons') && (
                            <>
                        {/* Metric Cards */}
                        <MetricCards
                            activeCoupons={data.active_coupons}
                            activeCouponsLimit={data.active_coupons_limit}
                            showActiveCouponsLimit={!isPremium}
                            conversionRate={data.conversion_rate}
                            totalViews={data.total_views}
                            totalRedemptions={data.total_redemptions}
                            bestPerforming={{
                                couponName: data.best_performing.coupon_name,
                                conversionRate: data.best_performing.conversion_rate,
                                redemptions: data.best_performing.redemptions,
                            }}
                            redemptionBreakdown={{
                                inApp: data.redemption_breakdown.in_app,
                                inStore: data.redemption_breakdown.in_store,
                            }}
                        />

                        {/* Premium insights bar */}
                        {isPremium && data?.location_analytics && (
                            <PremiumInsightsBar
                                peakDay={data.location_analytics.peak_day}
                                topLocation={data.location_analytics.top_location}
                                avgCivicPoints={data.location_analytics.avg_points_spent}
                                redemptionTrends={data.location_analytics.redemption_trends}
                                locationPerformance={data.location_analytics.location_performance}
                                userEngagement={data.location_analytics.user_engagement}
                            />
                        )}

                        {/* Filters Section */}
                        <FiltersSection
                            filters={data.filters}
                            onFiltersChange={handleFiltersChange}
                            onApplyFilters={handleApplyFilters}
                            onExportData={handleExportCSV}
                            onExportPDF={handleExportPDF}
                            onExportXLSX={handleExportXLSX}
                            onExportCSV={handleExportCSV}
                            showExport={isPremium}
                            showLocation={isPremium}
                            showDemographics={isPremium}
                        />

                        {/* Basic layout: Coupon Performance Comparison (with pagination) */}
                        {!isPremium && (
                            <div className="flex flex-col gap-[16px]">
                                <CouponPerformanceTable rows={pagedRows} />
                                <HeatmapPagination
                                    page={page}
                                    totalPages={totalPages}
                                    onPageChange={(next) => setPage(Math.min(Math.max(1, next), totalPages))}
                                />
                            </div>
                        )}

                        {/* Geographic Distribution Header */}
                        <div className="flex flex-col gap-[8px]">
                            <div className="text-[#111827] dark:text-white text-[20px] font-bold leading-[28px]" style={{ fontFamily: 'Nunito' }}>
                                Coupon Redemption Geographic Distribution
                            </div>
                            <div className="text-[#4A5565] dark:text-gray-400 text-[13px] font-normal leading-[19.5px]" style={{ fontFamily: 'Nunito' }}>
                                Interactive geographic distribution of coupon redemptions across city districts
                            </div>
                        </div>

                        {/* Geographic section */}
                        {isPremium ? (
                            <PremiumGeographicDistribution
                                locations={data.locations}
                                totalRedemptions={data.total_redemptions}
                                conversionRate={data.conversion_rate}
                                selectedLocation={data.selectedLocation}
                                onLocationSelect={handleLocationSelect}
                            />
                        ) : isFree ? null : (
                            <HeatMapSection
                                locations={data.locations}
                                selectedLocation={data.selectedLocation}
                                onLocationSelect={handleLocationSelect}
                                disableInteraction={isBasic || isFree}
                                useMockMap={isBasic || isFree}
                                overlay={
                                    isBasic ? (
                                        <PlanGateOverlay
                                            variant="basic_map"
                                            title="Unlock Geographic Insights"
                                            description="Upgrade to Premium to access:"
                                            features={[
                                                'Interactive heatmaps with zoom',
                                                'Neighborhood-level breakdown',
                                                'Top performing locations',
                                                'Compare districts side-by-side',
                                            ]}
                                            primaryActionLabel="Upgrade to Premium"
                                            onPrimaryAction={() => navigate('/merchant/my-plan')}
                                        />
                                    ) : null
                                }
                            />
                        )}

                        {/* Premium layout: Coupon Performance Comparison (placed AFTER geographic section, per HTML) */}
                        {isPremium && (
                            <div className="flex flex-col gap-[16px]">
                                <CouponPerformanceTable rows={couponRows} />
                            </div>
                        )}
                            </>
                        )}
                    </div>

                    {/* Free: lock content (header + body). Dropdown stays above. */}
                    {isFree && (
                        <PlanGateOverlay
                            variant="free_fullscreen"
                            title="Analytic Heat Map Locked"
                            description="Upgrade to our Basic or Premium plan to unlock advance analytics and heat map visualizations for your business."
                            features={[
                                'Interactive heat map analytics',
                                'Location-based insights',
                                'Advanced performance tracking',
                            ]}
                            primaryActionLabel="Upgrade Plan"
                            onPrimaryAction={() => navigate('/merchant/my-plan')}
                            secondaryActionLabel="Learn More"
                            onSecondaryAction={() => navigate('/merchant/my-plan')}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MerchantHeatmapPage;

